import styled, { css } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { DatetimePickerButton } from '../components/datetime-picker-button';
import { EmptyMessage } from '../components/empty-message';
import { Body2, Caption } from '../components/typography';
import { useDatetimeFormat } from '../utils/use-datetime-format';
import { useWidgetDimensions } from '../widget-dimensions-provider';
import type { DayWithTimeslots, Timeslot } from './datetime-provider';
import { useDatetime } from './datetime-provider';

type DaySlotsProps = {
  day?: DayWithTimeslots;
  displayReserved?: boolean;
  displayPast?: boolean;
  displayLocked?: boolean;
  displayUngrouped?: boolean;
  onClick: (timeslot: Timeslot) => void;
};

export function TimeSlotPicker({
  day,
  onClick,
  displayReserved,
  displayLocked,
  displayPast,
  displayUngrouped
}: DaySlotsProps) {
  const { t } = useLingui();
  const { formatTime } = useDatetimeFormat();
  const { getDayTimeslots } = useDatetime();
  const { textColor } = useSettings();
  const { isMobile } = useWidgetDimensions();

  const timeslots = getDayTimeslots(
    day,
    displayPast,
    displayReserved,
    displayLocked
  );

  if (!day || !timeslots || timeslots.length === 0) {
    return <EmptyMessage type="time-slots" />;
  }

  const renderGroup = (title: string, groupHours: [number, number]) => {
    const groupTimeslots = (timeslots || []).filter(({ from }) => {
      const hours = from.getHours();
      return hours >= groupHours[0] && hours < groupHours[1];
    });

    if (groupTimeslots.length === 0) {
      return null;
    }

    return (
      <Group>
        <Caption color={getColorWithAlpha(textColor, 0.5)}>{title}</Caption>
        <GroupItems>
          {groupTimeslots.map((slot) => {
            const disabled = slot.reserved || slot.past;
            return (
              <TimeContainer
                key={slot.from.toString()}
                disabled={disabled}
                onClick={() => (disabled ? undefined : onClick(slot))}
              >
                <Time _isMobile={isMobile}>{formatTime(slot.from)}</Time>
              </TimeContainer>
            );
          })}
        </GroupItems>
      </Group>
    );
  };

  if (displayUngrouped) {
    return <Container>{renderGroup('', [0, 24])}</Container>;
  }

  return (
    <Container>
      {renderGroup(t`Morning`, [0, 12])}
      {renderGroup(t`Afternoon`, [12, 17])}
      {renderGroup(t`Evening`, [17, 24])}
    </Container>
  );
}

const TimeContainer = styled(DatetimePickerButton).attrs({
  className: createReadableClass({
    component: 'time-slot-picker',
    element: 'time-container'
  })
})`
  width: 100px;
  height: 44px;
`;

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'time-slot-picker',
    element: 'container'
  })
})`
  display: flex;
  gap: 20px;
  flex-direction: column;
`;

const Group = styled.div.attrs({
  className: createReadableClass({
    component: 'time-slot-picker',
    element: 'group'
  })
})`
  display: flex;
  gap: 8px;
  flex-direction: column;
`;

const GroupItems = styled.div.attrs({
  className: createReadableClass({
    component: 'time-slot-picker',
    element: 'group-items'
  })
})`
  display: flex;
  gap: 8px;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Time = styled(Body2).attrs({
  className: createReadableClass({
    component: 'time-slot-picker',
    element: 'time'
  })
})<{ _isMobile: boolean }>`
  ${({ _isMobile }) =>
    _isMobile &&
    css`
      font-size: 14px;
    `}
`;
